import React, { useCallback, useMemo, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { listSittingsForVisit } from '../../services/api/patientPlanApi';
import SittingCard from '../procedures/SittingCard';
import FollowUpPicker from './FollowUpPicker';
import useSittingActions from '../../hooks/useSittingActions';

const TERMINAL = new Set(['completed', 'paid', 'cancelled']);

// A follow-up the prescription already set on this visit is an absolute date —
// only seed the picker from a real YYYY-MM-DD (never a parser duration string).
const isYmd = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

const isOpen = (s) =>
  !s?.is_historical && !TERMINAL.has(String(s?.status || '').toLowerCase());

/**
 * VisitCompletionPrompt — Req 6.
 *
 * Shown when a doctor completes a visit that still has open (non-terminal)
 * procedures, including ones delegated to a nurse. The visit CAN still complete
 * (visit/sitting decoupling is preserved) — this is a confirmation, not a block.
 *
 * Lists every open procedure with the usual per-procedure actions (Start / Note /
 * Reassign / Cancel via SittingCard + useSittingActions), and offers three
 * resolutions:
 *   - Complete procedures first — abort completion so the team finishes them now.
 *   - Cancel all & complete     — cancel the open procedures (one reason), then complete.
 *   - Mark for follow-up         — set the visit's follow-up (required date), then complete.
 *
 * Props:
 *   visit          — { visit_id, patient_name, first_name, last_name }
 *   viewer         — { memberId, role }
 *   initialOpenSittings — open sittings the caller already fetched (avoids a flash)
 *   updateVisit    — (visitId, payload) => Promise   (visit store action)
 *   cancelSitting  — (planId, sittingId, { reason }) => Promise  (sitting store action)
 *   onClose        — () => void  (abort — do NOT complete the visit)
 *   onCompleted    — (visitId) => void  (post-complete cleanup/log in the parent)
 */
export default function VisitCompletionPrompt({
  visit,
  viewer,
  variant = 'full',
  initialOpenSittings = [],
  updateVisit,
  cancelSitting,
  onClose,
  onCompleted,
}) {
  const { isDark } = useTheme();
  const visitId = visit?.visit_id;
  const patientName =
    visit?.patient_name
    || `${visit?.first_name || ''} ${visit?.last_name || ''}`.trim()
    || 'this patient';

  // 'followup-only' (all open procedures unpaid) — the only resolution is to set a
  // follow-up so the procedure carries to the next visit; no cancel/complete paths.
  const followUpOnly = variant === 'followup-only';

  // Pre-fill the follow-up from the one this visit already carries (set by the
  // prescription form). This keeps the completion follow-up and the prescription
  // follow-up as a SINGLE value — the doctor can still change it below.
  const seededFollowUp = isYmd(visit?.follow_up_date) ? visit.follow_up_date : '';

  const [sittings, setSittings] = useState(initialOpenSittings);
  const [mode, setMode] = useState(followUpOnly ? 'followup' : 'choose'); // 'choose' | 'cancel' | 'followup'
  const [reason, setReason] = useState('');
  const [followUpRequired, setFollowUpRequired] = useState(!!seededFollowUp);
  const [followUpDate, setFollowUpDate] = useState(seededFollowUp);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await listSittingsForVisit(visitId);
      // listSittingsForVisit → { success, data: { sittings: [...], count } }.
      const all = Array.isArray(r?.data?.sittings)
        ? r.data.sittings
        : (Array.isArray(r?.data) ? r.data : []);
      setSittings(all.filter(isOpen));
    } catch { /* keep the current list on a transient fetch error */ }
  }, [visitId]);

  // Per-procedure actions (Start / Note / Reassign / Cancel) — reuse the shared
  // orchestration so behaviour matches the queue surfaces. No navigation from here.
  const { handleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: refresh,
    navigate: () => {},
  });

  const open = useMemo(() => sittings.filter(isOpen), [sittings]);

  const finishComplete = useCallback(async (extra) => {
    setErr('');
    setBusy(true);
    try {
      await updateVisit(visitId, { status: 'COMPLETED', ...(extra || {}) });
      onCompleted?.(visitId);
    } catch (e) {
      setErr(e?.message || 'Could not complete the visit. Please try again.');
      setBusy(false);
    }
  }, [updateVisit, visitId, onCompleted]);

  const doCancelAll = useCallback(async () => {
    if (reason.trim().length < 3) { setErr('Please give a brief reason (min 3 characters).'); return; }
    setErr('');
    setBusy(true);
    for (const s of open) {
      try { await cancelSitting(s.plan_id, s.sitting_id, { reason: reason.trim() }); }
      catch { /* skip a failed cancel; the doctor can retry that row */ }
    }
    await finishComplete();
  }, [reason, open, cancelSitting, finishComplete]);

  const doFollowUp = useCallback(async () => {
    if (!followUpDate) { setErr('Please pick a follow-up date.'); return; }
    await finishComplete({ follow_up_required: true, follow_up_date: followUpDate });
  }, [followUpDate, finishComplete]);

  // If every procedure was closed individually (via the row actions), there's
  // nothing left to confirm — let the doctor just complete.
  const allClear = open.length === 0;

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1000, padding: '1rem',
    background: 'rgba(15,23,42,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const panel = {
    width: '100%', maxWidth: '34rem', maxHeight: '88vh', overflowY: 'auto',
    borderRadius: '0.75rem', padding: '1.1rem',
    background: isDark ? '#1e293b' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`,
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  };
  const btnPrimary = {
    padding: '0.5rem 0.85rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 700,
    border: '1px solid #0d9488', background: '#0d9488', color: '#fff', cursor: 'pointer',
  };
  const btnSecondary = {
    padding: '0.5rem 0.85rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 600,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#e2e8f0' : '#334155', cursor: 'pointer',
  };
  const btnDanger = {
    ...btnSecondary,
    border: '1px solid #dc2626', color: isDark ? '#fca5a5' : '#b91c1c',
    background: isDark ? '#0f172a' : '#fff',
  };
  const inputStyle = {
    width: '100%', marginTop: '0.4rem', padding: '0.45rem 0.6rem', borderRadius: '0.5rem',
    fontSize: '0.8125rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
  };

  return (
    <>
    <div role="dialog" aria-modal="true" style={overlay} onClick={busy ? undefined : onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
          {allClear ? 'Complete visit' : 'Open procedures on this visit'}
        </div>
        <div style={{ fontSize: '0.8125rem', color: isDark ? '#cbd5e1' : '#475569', margin: '0.4rem 0 0.8rem' }}>
          {allClear
            ? `All procedures for ${patientName} are handled. You can complete the visit.`
            : followUpOnly
              ? `${patientName} has a procedure that isn't paid yet. Set a follow-up so it carries to the next visit, then complete.`
              : `${patientName} still has open procedures (including any delegated to a nurse). Please confirm this is intentional — you can finish them, cancel them, or mark a follow-up.`}
        </div>

        {!allClear && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.9rem' }}>
            {open.map((s) => (
              <SittingCard
                key={s.sitting_id}
                sitting={s}
                viewer={viewer}
                onAction={handleAction}
                dense
              />
            ))}
          </div>
        )}

        {err && (
          <div style={{ fontSize: '0.75rem', color: isDark ? '#fca5a5' : '#b91c1c', marginBottom: '0.5rem' }}>
            {err}
          </div>
        )}

        {mode === 'cancel' && !allClear && (
          <div style={{ marginBottom: '0.8rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#e2e8f0' : '#334155' }}>
              Reason for cancelling these procedures
            </label>
            <input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Patient declined / not needed"
              style={inputStyle}
            />
          </div>
        )}

        {mode === 'followup' && !allClear && (
          <div style={{ marginBottom: '0.8rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#e2e8f0' : '#334155' }}>
              Follow-up (required) — the open procedures stay and show up for the next visit
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
              <FollowUpPicker
                required={followUpRequired}
                date={followUpDate}
                onChange={(req, d) => { setFollowUpRequired(req); setFollowUpDate(d || ''); }}
                isDark={isDark}
                size="comfortable"
              />
            </div>
            {seededFollowUp && followUpDate === seededFollowUp && (
              <div style={{ fontSize: '0.6875rem', color: isDark ? '#94a3b8' : '#64748b', marginTop: '0.35rem' }}>
                Using the follow-up already set on this visit (from the prescription). Change it above if needed.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '0.5rem' }}>
          {allClear ? (
            <>
              <button type="button" style={btnSecondary} disabled={busy} onClick={onClose}>Not yet</button>
              <button type="button" style={btnPrimary} disabled={busy} onClick={() => finishComplete()}>
                {busy ? 'Completing…' : 'Complete visit'}
              </button>
            </>
          ) : mode === 'choose' ? (
            <>
              <button type="button" style={btnSecondary} disabled={busy} onClick={onClose}>
                Complete procedures first
              </button>
              <button type="button" style={btnDanger} disabled={busy} onClick={() => { setErr(''); setMode('cancel'); }}>
                Cancel all & complete
              </button>
              <button type="button" style={btnPrimary} disabled={busy} onClick={() => { setErr(''); setMode('followup'); }}>
                Mark for follow-up
              </button>
            </>
          ) : mode === 'cancel' ? (
            <>
              <button type="button" style={btnSecondary} disabled={busy} onClick={() => { setErr(''); setMode('choose'); }}>
                Back
              </button>
              <button type="button" style={btnDanger} disabled={busy} onClick={doCancelAll}>
                {busy ? 'Working…' : 'Cancel all & complete'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                style={btnSecondary}
                disabled={busy}
                onClick={followUpOnly ? onClose : () => { setErr(''); setMode('choose'); }}
              >
                {followUpOnly ? 'Not now' : 'Back'}
              </button>
              <button type="button" style={btnPrimary} disabled={busy} onClick={doFollowUp}>
                {busy ? 'Working…' : 'Save follow-up & complete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    {dialogs}
    </>
  );
}
